import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NzDropDownDirective, NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzMenuDirective, NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { MediaLimits } from './media-api';
import { failureData, MEDIA_ERROR_KEYS, MEDIA_TEXTS, mediaErrorText, Translate } from './media-errors';
import { formatClockMs, formatDimensions, formatSize, pageLang } from './media-format';
import { MediaIconComponent } from './media-icon.component';
import { MediaThumbComponent } from './media-thumb.component';
import { Tile } from './media-tiles';

/**
 * One tile of the grid (the boards AdmMediaEditor and AdmMediaStates): the picture with its number, the handle, the «Главное» tag and, by state, the progress, the
 * work in progress, the delay, the error. It draws and tells what the editor pressed; the page does the rest.
 */
@Component({
  selector: 'hc-media-tile',
  imports: [TranslocoDirective, NzDropDownDirective, NzDropdownMenuComponent, NzMenuDirective, NzMenuItemComponent, MediaIconComponent, MediaThumbComponent],
  templateUrl: './media-tile.component.html',
  styleUrls: ['./media-tile.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediaTileComponent {
  readonly tile = input.required<Tile>();
  /** The tile is the last one that can be moved right. */
  readonly last = input(false);
  /** The editor may change things. */
  readonly editable = input(false);
  /** A request for this item is on its way. */
  readonly busy = input(false);
  /** The order is being saved: nothing can be moved now. */
  readonly orderBusy = input(false);
  readonly dropOver = input(false);
  /** The limits of the server, for the texts of the errors that name them (a photo under 600 px, a video over 30 seconds). */
  readonly limits = input<MediaLimits | null>(null);

  readonly moveLeft = output<void>();
  readonly moveRight = output<void>();
  readonly makeMain = output<void>();
  readonly frame = output<void>();
  readonly replace = output<void>();
  readonly remove = output<void>();
  readonly retry = output<void>();
  readonly retryUpload = output<void>();
  readonly cancelUpload = output<void>();
  readonly retryReplacement = output<void>();
  readonly cancelReplacement = output<void>();

  private readonly translate = inject(TranslocoService);

  /** The tile can be picked up and moved: it is an item of the server, nothing is going on with it, the editor may. */
  protected readonly movable = computed(() => {
    const t = this.tile();
    return this.editable() && t.id !== null && !this.busy() && !this.orderBusy() && (t.state === 'ready' || t.state === 'processing' || t.state === 'delayed' || t.state === 'failed');
  });

  /** `0:18` on the video badge. */
  protected readonly duration = computed(() => {
    const ms = this.tile().durationMs;
    return ms != null ? formatClockMs(ms) : '';
  });

  /** The line under the picture: what the item is, and the facts about it. */
  protected readonly kindWord = computed(() => this.t(this.tile().kind === 'VIDEO' ? 'tile.video' : 'tile.photo'));

  protected readonly info = computed(() => {
    const t = this.tile();
    const lang = pageLang(this.translate.getActiveLang());
    if (t.state === 'failed') {
      return t.uploadFailure ? t.uploadFailure : t.errorKey && MEDIA_ERROR_KEYS.includes(t.errorKey) ? mediaErrorText(this.text, t.errorKey, this.dataOf(t.errorKey), lang) : this.t('tile.processFailed');
    }
    if (t.state === 'uploading' || t.state === 'queued' || t.state === 'interrupted') {
      return t.fileName ?? '';
    }
    if (t.kind === 'VIDEO') {
      const parts = [t.durationMs != null ? formatClockMs(t.durationMs) : '', this.t(t.state === 'ready' ? 'tile.noSound' : 'tile.removeSound')];
      return parts.filter(Boolean).join(' · ');
    }
    if (t.state === 'ready' && t.maxWidth != null) {
      return this.t('tile.px', { px: t.maxWidth });
    }
    return t.width != null && t.height != null ? formatDimensions(t.width, t.height) : '';
  });

  protected readonly sentText = computed(() => {
    const t = this.tile();
    if (t.state === 'queued') {
      return this.t('tile.queued');
    }
    const lang = pageLang(this.translate.getActiveLang());
    return t.size != null ? this.t('tile.sent', { sent: formatSize(t.sent ?? 0, lang), size: formatSize(t.size, lang) }) : '';
  });

  /** The tooltip of the tile: the file name (the tail of an error is never shown). */
  protected readonly hint = computed(() => this.tile().fileName ?? '');

  protected readonly replacementText = computed(() => {
    const r = this.tile().replacement;
    if (!r) {
      return '';
    }
    const lang = pageLang(this.translate.getActiveLang());
    switch (r.state) {
      case 'uploading':
        return this.t('tile.replacing.uploading', { percent: r.percent ?? 0 });
      case 'failed':
        return r.errorKey && MEDIA_ERROR_KEYS.includes(r.errorKey) ? mediaErrorText(this.text, r.errorKey, this.dataOf(r.errorKey), lang) : this.t('tile.replacing.failed');
      case 'delayed':
        return this.t('tile.replacing.delayed');
      default:
        return this.t('tile.replacing.processing');
    }
  });

  private dataOf(key: string): Record<string, unknown> {
    const l = this.limits();
    return failureData(key, { photoMinSide: l?.photoMinSide ?? 600, videoMinSide: l?.videoMinSide ?? 720, videoSeconds: l?.videoSeconds ?? 30 });
  }

  private readonly text: Translate = (key, params) => this.translate.translate(key, params);

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.translate(`${MEDIA_TEXTS}.${key}`, params);
  }
}
